<template>
  <div class="row">
    <h4 class="col-2">Material</h4>

    <div class="col text-end">
      <button
        type="button"
        class="btn btn-success btn-sm ms-1 me-2 mb-2"
        v-show="showCommissionForAll"
        @click="asyncCreateCommissionForAll"
        :disabled="materials.length < 1 || disableCommission"
      >
        Zahtevaj komisioniranje celotnega materiala
      </button>
      <button
        type="button"
        class="btn btn-success btn-sm ms-1 me-2 mb-2"
        v-show="showCommission"
        @click="addMaterialsCommissionAsync"
        :disabled="disableCommission"
      >
        Zahtevaj komisioniranje
      </button>
      <button
        type="button"
        class="btn btn-info btn-sm me-2 mb-2"
        v-show="canChange && enableTemplates"
        @click="showBomTemplates = true"
      >
        <span class="d-none d-md-inline">Dodaj material</span> iz predloge
      </button>
      <button
        type="button"
        class="btn btn-warning btn-sm me-2 mb-2"
        v-show="canChange"
        @click="sectionForAddMaterial = { order: 1000, name: 'Ročno dodan' }"
      >
        Dodaj <span class="d-none d-md-inline">material</span>
      </button>
      <button
        type="button"
        class="btn btn-secondary btn-sm me-2 mb-2"
        v-show="canChange && enableBarcode"
        @click="sectionForAddMaterialBarcode = { order: 1100, name: 'Čitalec' }"
      >
        Bralnik <span class="d-none d-md-inline">črtne kode (material)</span>
      </button>
    </div>
  </div>

  <MaterialsList
    v-model="materials"
    :canChange="canChange"
    :showCommission="showCommission"
    :showMissingCommission="showMissingCommission"
    :showWarehouse="showWarehouse"
    :showSection="showSection"
    :showSelect="showSelect"
    :showStock="showStock"
    :sort="sort"
    :showNotPartOfOffer="showNotPartOfOffer"
    @selectMaterial="
      (m) => {
        if (canChange) {
          materialForActions = m
        }
      }
    "
    @selectSection="
      (s) => {
        sectionForAddMaterial = s
      }
    "
    @changeSelection="(e) => $emit('changeSelection', e)"
    @sort="
      (e) => {
        sort.by = e.by
        sort.dir = e.dir
      }
    "
  />

  <BomTemplates
    :visible="showBomTemplates"
    :quantity="quantity"
    @close="showBomTemplates = false"
    @add="addMaterialsAsync"
  />

  <WorkOrderMaterialActions
    v-model="materialForActions"
    @close="
      (e) => {
        if (e === true) {
          loadMaterialsAsync(workOrderType, workOrderId)
        }
        materialForActions = null
      }
    "
    @changed="$emit('changed')"
    :canChange="canChange"
    :showWarehouse="showWarehouse"
    :workOrderType="workOrderType"
    :workOrderId="workOrderId"
    :showOrderMaterial="showOrderMaterial"
    :showNotPartOfOffer="showNotPartOfOffer"
    :canDeleteMaterial="canDeleteMaterial"
  />

  <SelectMaterial
    v-if="canChange"
    :quantity="quantity"
    :useBarcode="true"
    :serialsRequired="serialsRequired"
    :section="sectionForAddMaterialBarcode"
    :visible="!!sectionForAddMaterialBarcode"
    :roundUp="roundUp"
    @close="sectionForAddMaterialBarcode = null"
    @add="addMaterialsAsync"
  />

  <SelectMaterial
    v-if="canChange"
    :quantity="quantity"
    :serialsRequired="serialsRequired"
    :section="sectionForAddMaterial"
    :visible="!!sectionForAddMaterial"
    :roundUp="roundUp"
    @close="sectionForAddMaterial = null"
    @add="addMaterialsAsync"
  />
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useAuth } from "../composition-api/useAuth"
import { setError, startLoading, stopLoading, setSuccess, setWarning } from "./Notification"
import WorkOrderMaterialActions from "./WorkOrderMaterialActions.vue"
import BomTemplates from "../components/BomTemplates.vue"
import SelectMaterial from "../components/SelectMaterial.vue"
import MaterialsList from "../components/MaterialsList.vue"
import { getEntityAsync, saveMaterialsAsync } from "../api"

defineOptions({ name: "WorkOrderMaterials" })

const props = defineProps({
  canChange: Boolean,
  workOrderType: { type: String, required: true },
  workOrderId: { type: String },
  quantity: { type: Number, default: 1 },
  enableTemplates: { type: Boolean, default: false },
  enableBarcode: { type: Boolean, default: true },
  showWarehouse: { type: Boolean, default: false },
  showSection: { type: Boolean, default: true },
  showSelect: { type: Boolean, default: false },
  showCommission: { type: Boolean, default: false },
  showCommissionForAll: { type: Boolean, default: false },
  showMissingCommission: { type: Boolean, default: false },
  disableCommission: { type: Boolean, default: false },
  showStock: { type: Boolean, default: false },
  showOrderMaterial: { type: Boolean, default: false },
  serialsRequired: { type: Boolean, default: true },
  showNotPartOfOffer: { type: Boolean, default: false },
  canDeleteMaterial: { type: Boolean, default: true },
  roundUp: { type: Boolean, default: false },
})

const emit = defineEmits(["changeSelection", "commissioned", "changed"])

const { fetchAuthAsync } = useAuth()
const materialForActions = ref(null)
const sectionForAddMaterial = ref(null)
const sectionForAddMaterialBarcode = ref(null)
const showBomTemplates = ref(false)
const materials = ref([])
const sort = ref({ by: "", dir: "" })

const loadMaterialsAsync = async (materialRelation, relationId) => {
  const sortParams = sort.value?.by ? `?sortBy=${sort.value.by}&sortDir=${sort.value.dir}` : ""
  materials.value =
    (await getEntityAsync(fetchAuthAsync, `Materials/${materialRelation}`, relationId, sortParams)) ?? []
}

const addMaterialsAsync = async (m) => {
  const materialsToAdd = []
  m.forEach((m) => {
    const found = materials.value.find((e) => e.materialID === m.materialID)
    if (!found) {
      m.isNew = true
      materials.value.push(m)
      materialsToAdd.push(m)
    } else {
      if (found.isForDelete) {
        found.quantity = m.quantity
        found.isForDelete = false
      } else {
        // Skip duplicates
        const existingSerials = new Set(
          (found.serialNumbers || "")
            .split("\n")
            .map((l) => l.split(";")[0]?.trim().toLowerCase())
            .filter(Boolean),
        )
        const incomingLines = (m.serialNumbers || "").split("\n").filter((l) => l.trim())
        const newLines = incomingLines.filter((line) => {
          const serial = line.split(";")[0]?.trim().toLowerCase()
          return serial && !existingSerials.has(serial)
        })
        if (incomingLines.length - newLines.length > 0) {
          setWarning(`Serijske številke že obstajajo — preskočeno.`)
        }
        found.quantity += m.quantity
        const existingSerial = (found.serialNumbers || "").replace(/\n+$/, "")
        found.serialNumbers = existingSerial + (newLines.length > 0 ? "\n" + newLines.join("\n") : "")
      }
      found.warehouseID = m.warehouseID
      found.isDirty = true
      materialsToAdd.push(found)
    }
  })
  const result = await saveMaterialsAsync(fetchAuthAsync, materialsToAdd, props.workOrderType, props.workOrderId)
  if (result) {
    await loadMaterialsAsync(props.workOrderType, props.workOrderId)
    emit("changed")
    setSuccess("Material dodan..", true)
    showBomTemplates.value = false
  } else {
    console.error("materials not saved.")
  }
}

const addMaterialsCommissionAsync = async () => {
  const commissionMaterials = JSON.parse(JSON.stringify(materials.value.filter((m) => m.commissionQuantity > 0)))
  if (commissionMaterials.length === 0) {
    return
  }
  commissionMaterials.forEach((m) => {
    m.quantity = m.commissionQuantity
    m.isNew = true
  })
  const result = await saveMaterialsAsync(
    fetchAuthAsync,
    commissionMaterials,
    `Commission${props.workOrderType}`,
    props.workOrderId,
  )
  if (result) {
    emit("commissioned")
    setSuccess("Zahtevek za komisioniranje oddan.", true)
    await loadMaterialsAsync(props.workOrderType, props.workOrderId)
  } else {
    setError("Komisioniranje ni bilo uspešno.")
  }
}

const asyncCreateCommissionForAll = async () => {
  materials.value.forEach(async (m) => (m.commissionQuantity = m.quantity))
  await addMaterialsCommissionAsync()
}

watch(
  () => props.workOrderId,
  async (newId) => {
    if (newId) {
      await loadMaterialsAsync(props.workOrderType, props.workOrderId)
    }
  },
  { immediate: true },
)

watch(
  () => sort.value,
  async (newSort) => {
    await loadMaterialsAsync(props.workOrderType, props.workOrderId)
  },
  { deep: true },
)

const reload = async () => {
  await loadMaterialsAsync(props.workOrderType, props.workOrderId)
}

defineExpose({ reload })
</script>
